import type { MouseEvent } from 'react';
import { memo } from 'react';
import type { ListItemProps } from '@mui/joy/ListItem';
import BaseListItem from '@mui/joy/ListItem';
import { clsx } from 'clsx';
import { useSlot } from '../../slots';
import { Box } from '../Box';
import type { IconComponentType } from '../../types/index';
import { slowCn } from '../../lib/utils';
import type { TooltipProps } from '../Tooltip';
import { Tooltip } from '../Tooltip';
import { menuItemHelpTextVariants, menuItemIconVariants, menuItemTitleVariants } from './styles';
import MenuContent from './MenuContent';

export enum MenuItemHelpTextPosition {
  Inline = 'inline',
  InTooltip = 'tooltip',
}
export type MenuItemProps = {
  startDecoratorComponent?: IconComponentType;
  startDecoratorNode?: React.ReactNode;
  endDecoratorComponent?: IconComponentType;
  endDecoratorNode?: React.ReactNode;
  title: string;
  caption?: string;
  helpTextDetails?: {
    text: string | React.ReactNode;
    position: MenuItemHelpTextPosition;
    placement?: TooltipProps['placement'];
  };
  className?: string;
  onClick?: (event: MouseEvent) => void;
  onMouseOver?: (event: MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  selected?: boolean;
  size?: 'sm' | 'md';
  titleClassName?: string;
  slots?: {
    menuContentItem?: React.ElementType;
  };
};

const MenuItem = memo(function MenuItem(props: MenuItemProps) {
  const {
    title,
    helpTextDetails,
    className,
    onClick,
    onMouseOver,
    variant = 'default',
    startDecoratorComponent: StartDecoratorComponent,
    startDecoratorNode,
    endDecoratorComponent: EndDecoratorComponent,
    endDecoratorNode,
    disabled = false,
    selected,
    size = 'md',
    slots,
    caption,
    titleClassName: _titleClassName,
  } = props;

  const iconClassName = menuItemIconVariants({ variant, disabled });
  const titleClassName = menuItemTitleVariants({ variant, disabled, className: _titleClassName });
  const helpTextClassName = menuItemHelpTextVariants({ variant, disabled });

  const startDecorator = StartDecoratorComponent ? (
    <StartDecoratorComponent className={iconClassName} height={16} width={16} />
  ) : (
    startDecoratorNode
  );

  const endDecorator = EndDecoratorComponent ? (
    <EndDecoratorComponent
      className={clsx('justify-self-end', iconClassName)}
      height={16}
      width={16}
    />
  ) : (
    endDecoratorNode
  );

  const [MenuContentItem, menuContentItemProps] = useSlot('menuContentItem', {
    elementType: MenuContent,
    className: slowCn(
      'row bg-primary hover:bg-secondary_hover my-xxs mx-sm ps-md pe-md flex items-center rounded-sm',
      { 'cursor-pointer': onClick },
      { 'pointer-events-none': disabled },
      { 'bg-quaternary': selected },
      { 'py-md !h-9': size === 'md' },
      { 'py-sm !h-7 !min-h-7': size === 'sm' },
      className,
    ),
    additionalProps: {
      startDecorator,
      endDecorator,
      title,
      titleClassName,
      helpTextDetails,
      helpTextClassName,
      caption,
      size,
      onClick,
      onMouseOver,
      disabled,
      selected,
      className,
    },
    externalForwardedProps: { slots },
    ownerState: {},
  });

  const el = <MenuContentItem {...menuContentItemProps} />;

  if (helpTextDetails?.position === MenuItemHelpTextPosition.InTooltip) {
    return (
      <Tooltip
        arrow={false}
        offset={6}
        placement={helpTextDetails.placement}
        title={helpTextDetails.text}
      >
        <Box className="h-full w-full">{el}</Box>
      </Tooltip>
    );
  }

  return el;
});
export { BaseListItem };
export default MenuItem;
